import React, { Fragment, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import core from '@gin-betting/core';
import { omit } from 'lodash';

import { OrganizationIcon } from 'components';
import globalMessages from 'messages';
import ui from 'modules/ui';
import {
    ALL,
    OverlayType,
    UNGROUPED_CATEGORIES_KEY,
} from '../../../../constants';
import { CategorizerItemByOrganizations } from '../../../../types';
import messages from '../../../Categorizer/messages';
import { MobileFiltersCtx } from '../../MobileFiltersCtx';
import { SportSummaryRow } from '../SportSummaryRow';
import TournamentItem from '../TournamentItem';
import TournamentItemWrapper from '../TournamentItemWrapper';

interface Props {
    categorizerItemsByOrganizations:
        | CategorizerItemByOrganizations[]
        | undefined;
    sportItemCount: number;
    selectOrganization: (organizationName: string) => void;
}

const {
    utils: { setSearchString, getAppliedFilters },
} = core;

const {
    actions: { setActiveMobileCountryKey },
} = ui;

const OrganizationList: React.FC<Props> = ({
    categorizerItemsByOrganizations,
    sportItemCount,
    selectOrganization,
}) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const { setOverlay } = useContext(MobileFiltersCtx);

    const onClickAll = useCallback(() => {
        const obj = getAppliedFilters();

        const fieldsToOmit: Array<keyof typeof obj> = [
            'tournamentId',
            'tournamentIds',
            'sportIds',
        ];

        dispatch(setActiveMobileCountryKey(ALL));

        setSearchString(omit(obj, fieldsToOmit));
        setOverlay(OverlayType.NONE);
    }, [dispatch, setOverlay]);

    const goToTournamentsList = useCallback(
        (organizationName: string) => () =>
            selectOrganization(organizationName),
        [selectOrganization]
    );

    return (
        <Fragment>
            <TournamentItemWrapper onClick={onClickAll}>
                <TournamentItem
                    count={sportItemCount}
                    title={formatMessage(messages.selectAll)}
                />
            </TournamentItemWrapper>
            <SportSummaryRow />
            {categorizerItemsByOrganizations?.map(({ name, count, logo }) => {
                const title =
                    name === UNGROUPED_CATEGORIES_KEY
                        ? formatMessage(globalMessages.other)
                        : name;

                return (
                    <TournamentItemWrapper
                        key={name}
                        onClick={goToTournamentsList(name)}
                    >
                        <TournamentItem count={count} title={title}>
                            <OrganizationIcon logo={logo} />
                        </TournamentItem>
                    </TournamentItemWrapper>
                );
            })}
        </Fragment>
    );
};

export default React.memo(OrganizationList);
